/**
 * Utility to play notification sounds
 */

/**
 * Play a notification sound using Web Audio API
 * Creates a pleasant notification beep sound
 */
export const playNotificationSound = () => {
  try {
    // Create audio context
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Create oscillator for the sound
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    // Connect nodes
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // Configure sound - pleasant notification beep
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime); // Start at 800Hz
    oscillator.frequency.exponentialRampToValueAtTime(600, audioContext.currentTime + 0.1); // Drop to 600Hz
    
    // Volume envelope - quick fade in/out
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.01); // Quick fade in
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15); // Fade out
    
    // Play the sound
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.15);
    
    // Clean up
    oscillator.onended = () => {
      audioContext.close();
    };
  } catch (error) {
    console.warn('Could not play notification sound:', error);
    // Fallback: Try using HTML5 Audio with a data URI
    try {
      // Create a simple beep using data URI (very short base64 encoded audio)
      // This is a minimal fallback
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZijcIG2m98OSfUxAMUKfj8LZjHAY4kdfyzHksBSR3x/DdkEAKFF606euoVRQKRp/g8r5sIQUrgc7y2Yo3CBtpvfDkn1MQDFCn4/C2YxwGOJHX8sx5LAUkd8fw3ZBAC');
      audio.volume = 0.3;
      audio.play().catch(() => {
        // Ignore errors if audio can't play
      });
    } catch (fallbackError) {
      // If all else fails, silently fail
      console.warn('Notification sound fallback also failed:', fallbackError);
    }
  }
};

/**
 * Play a specific sound for boosting requests
 * Slightly different tone to distinguish it
 */
export const playBoostingRequestSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Create two oscillators for a more distinctive sound
    const oscillator1 = audioContext.createOscillator();
    const oscillator2 = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    // Connect both oscillators to gain node
    oscillator1.connect(gainNode);
    oscillator2.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // First beep - higher pitch
    oscillator1.frequency.setValueAtTime(1000, audioContext.currentTime);
    oscillator1.frequency.exponentialRampToValueAtTime(800, audioContext.currentTime + 0.08);
    
    // Second beep - lower pitch (starts slightly after first)
    oscillator2.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
    oscillator2.frequency.exponentialRampToValueAtTime(500, audioContext.currentTime + 0.18);
    
    // Volume envelope
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.4, audioContext.currentTime + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.25);
    
    // Play both sounds
    oscillator1.start(audioContext.currentTime);
    oscillator1.stop(audioContext.currentTime + 0.08);
    
    oscillator2.start(audioContext.currentTime + 0.1);
    oscillator2.stop(audioContext.currentTime + 0.18);
    
    // Clean up
    setTimeout(() => {
      audioContext.close();
    }, 300);
  } catch (error) {
    console.warn('Could not play boosting request sound:', error);
    // Fallback to regular notification sound
    playNotificationSound();
  }
};
